import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminGetUser, adminUpdateKYC, adminBlockUser, adminReviewAadhaar } from "@/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, User, Phone, Mail, Shield, CreditCard, FileText, Briefcase,
  CheckCircle, Clock, XCircle, AlertTriangle, IndianRupee, Calendar,
  Download, Eye, Ban, ShieldCheck, TrendingUp, Loader2, ChevronDown,
  Building, MapPin, Banknote, Users
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => { loadUser(); }, [userId]);

  const loadUser = async () => {
    try {
      const res = await adminGetUser(userId);
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load user details");
      navigate('/admin/dashboard');
    } finally { setLoading(false); }
  };

  const handleKYCUpdate = async (status) => {
    setActionLoading('kyc');
    try {
      await adminUpdateKYC(userId, status);
      toast.success(`KYC status updated to ${status}`);
      loadUser();
    } catch (err) { toast.error("Failed to update KYC"); }
    finally { setActionLoading(null); }
  };

  const handleBlock = async () => {
    if (!window.confirm("Are you sure you want to block/unblock this user?")) return;
    setActionLoading('block');
    try {
      await adminBlockUser(userId);
      toast.success("User status updated");
      loadUser();
    } catch (err) { toast.error("Failed to update user"); }
    finally { setActionLoading(null); }
  };

  const handleAadhaarApprove = async () => {
    if (!window.confirm("Approve this customer's Aadhaar? This marks them as Aadhaar-verified.")) return;
    setActionLoading('aadhaar');
    try {
      await adminReviewAadhaar(userId, 'approve', null);
      toast.success("Aadhaar approved");
      loadUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve Aadhaar");
    } finally { setActionLoading(null); }
  };

  const handleAadhaarReject = async () => {
    const reason = window.prompt("Reason for rejection (will be shown to customer):", "Aadhaar images are unclear. Please re-upload.");
    if (!reason) return;
    setActionLoading('aadhaar');
    try {
      await adminReviewAadhaar(userId, 'reject', reason);
      toast.success("Aadhaar rejected — customer notified");
      loadUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject Aadhaar");
    } finally { setActionLoading(null); }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);
  const formatDate = (d) => { if (!d) return 'N/A'; try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d?.substring(0, 10); } };

  const StatusBadge = ({ status, size = 'sm' }) => {
    const styles = {
      verified: 'bg-green-100 text-green-700', approved: 'bg-green-100 text-green-700',
      paid: 'bg-green-100 text-green-700', success: 'bg-green-100 text-green-700',
      disbursed: 'bg-cyan-100 text-cyan-700', transferred: 'bg-cyan-100 text-cyan-700',
      pending: 'bg-amber-100 text-amber-700', active: 'bg-blue-100 text-blue-700',
      rejected: 'bg-red-100 text-red-700', failed: 'bg-red-100 text-red-700',
      overdue: 'bg-red-100 text-red-700', blocked: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full font-semibold capitalize ${size === 'sm' ? 'text-[10px]' : 'text-xs'} ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status || 'unknown'}
      </span>
    );
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
  );

  if (!data) return null;
  const { user: profile, documents, subscriptions, applications, disbursements, repayments, emi_schedules, agent, summary } = data;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'applications', label: 'Applications', count: applications?.length },
    { id: 'repayments', label: 'Repayments', count: repayments?.length },
    { id: 'documents', label: 'Documents', count: documents?.length },
    { id: 'emis', label: 'EMI Schedule', count: emi_schedules?.length },
  ];

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Users
      </button>

      {/* Profile Header */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-[#0A2540] to-[#1565C0] p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <User className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{profile.name || 'Unnamed User'}</h2>
                <div className="flex items-center gap-4 mt-1 text-white/80 text-sm">
                  <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> +91 {profile.mobile}</span>
                  {profile.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {profile.email}</span>}
                </div>
                <p className="text-white/50 text-xs mt-1">ID: {profile.id?.substring(0, 8)}... • Joined {formatDate(profile.created_at)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={profile.kyc_status} size="md" />
              <StatusBadge status={profile.status || 'active'} size="md" />
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-gray-100">
          {[
            { label: 'Total Borrowed', value: formatCurrency(summary?.total_borrowed), icon: TrendingUp, color: 'text-blue-600' },
            { label: 'Total Repaid', value: formatCurrency(summary?.total_repaid), icon: CheckCircle, color: 'text-green-600' },
            { label: 'Outstanding', value: formatCurrency(summary?.total_due), icon: Clock, color: 'text-amber-600' },
            { label: 'Applications', value: summary?.applications_count || 0, icon: FileText, color: 'text-purple-600' },
            { label: 'Documents', value: summary?.documents_count || 0, icon: FileText, color: 'text-indigo-600' },
          ].map((stat, i) => (
            <div key={i} className="bg-white p-4 text-center">
              <stat.icon className={`h-5 w-5 mx-auto mb-1 ${stat.color}`} />
              <p className="text-lg font-bold text-gray-900">{stat.value}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Admin Actions */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Admin Actions</h3>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => handleKYCUpdate('verified')} disabled={actionLoading === 'kyc' || profile.kyc_status === 'verified'}
            className="bg-green-600 hover:bg-green-700 text-white rounded-xl text-xs">
            <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Verify KYC
          </Button>
          <Button size="sm" onClick={() => handleKYCUpdate('pending')} disabled={actionLoading === 'kyc'}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs">
            <Clock className="h-3.5 w-3.5 mr-1" /> Set Pending
          </Button>
          <Button size="sm" onClick={() => handleKYCUpdate('rejected')} disabled={actionLoading === 'kyc'}
            className="bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs">
            <XCircle className="h-3.5 w-3.5 mr-1" /> Reject KYC
          </Button>
          <Button size="sm" onClick={handleBlock} disabled={actionLoading === 'block'} variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50 rounded-xl text-xs">
            <Ban className="h-3.5 w-3.5 mr-1" /> {profile.status === 'blocked' ? 'Unblock' : 'Block'} User
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
              activeTab === tab.id ? 'bg-white text-[#0A2540] shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {tab.label}
            {tab.count > 0 && <span className="px-1.5 py-0.5 rounded-full bg-gray-200 text-[10px]">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        {activeTab === 'overview' && (
          <div className="p-6 space-y-6">
            {/* Personal Details */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Personal Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: 'Name', value: profile.name },
                  { label: 'Mobile', value: `+91 ${profile.mobile}` },
                  { label: 'Date of Birth', value: formatDate(profile.dob) },
                  { label: 'Gender', value: profile.gender || 'N/A' },
                  { label: 'Address', value: profile.address || 'N/A' },
                  { label: 'City', value: profile.city || 'N/A' },
                ].map((item, i) => (
                  <div key={i}>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{item.label}</p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">{item.value || 'N/A'}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* KYC Details */}
            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">KYC Verification</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">PAN Number</p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">{profile.pan_number || 'N/A'}</p>
                  <StatusBadge status={profile.pan_verified ? 'verified' : 'pending'} />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Aadhaar</p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5 font-mono">
                    {profile.aadhaar_number ? `XXXX XXXX ${String(profile.aadhaar_number).slice(-4)}` : 'Not provided'}
                  </p>
                  <StatusBadge status={profile.aadhaar_verified ? 'verified' : (profile.aadhaar_uploaded ? 'pending' : 'rejected')} />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Overall KYC</p>
                  <StatusBadge status={profile.kyc_status || 'pending'} size="md" />
                </div>
              </div>

              {/* Aadhaar manual-review panel */}
              {(profile.aadhaar_uploaded || profile.aadhaar_verified || documents?.some(d => (d.doc_type || '').startsWith('aadhaar'))) && (
                <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50/50 p-4" data-testid="aadhaar-review-panel">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-600">Aadhaar Review</h4>
                    <StatusBadge
                      status={profile.aadhaar_verified ? 'verified' : (profile.aadhaar_uploaded ? 'pending' : 'rejected')}
                      size="md"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    {['aadhaar_front', 'aadhaar_back'].map(side => {
                      const doc = documents?.find(d => d.doc_type === side);
                      const label = side === 'aadhaar_front' ? 'Front' : 'Back';
                      return (
                        <div key={side} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                          <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-50">
                            Aadhaar &mdash; {label}
                          </div>
                          {doc?.file_url ? (
                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="block" data-testid={`admin-aadhaar-${side}-link`}>
                              <img src={doc.file_url} alt={`Aadhaar ${label}`} className="w-full h-48 object-contain bg-gray-100 hover:opacity-90" />
                            </a>
                          ) : (
                            <div className="h-48 flex items-center justify-center text-xs text-gray-400 bg-gray-100">Not uploaded</div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {profile.aadhaar_rejection_reason && (
                    <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800" data-testid="aadhaar-rejection-display">
                      <strong>Last rejection:</strong> {profile.aadhaar_rejection_reason}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={handleAadhaarApprove}
                      disabled={actionLoading === 'aadhaar' || profile.aadhaar_verified || !profile.aadhaar_uploaded}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-xl text-xs"
                      data-testid="aadhaar-approve-btn"
                    >
                      <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Approve Aadhaar
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleAadhaarReject}
                      disabled={actionLoading === 'aadhaar' || !profile.aadhaar_uploaded}
                      className="bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs"
                      data-testid="aadhaar-reject-btn"
                    >
                      <XCircle className="h-3.5 w-3.5 mr-1" /> Reject Aadhaar
                    </Button>
                    {!profile.aadhaar_uploaded && !profile.aadhaar_verified && (
                      <span className="text-xs text-gray-500 self-center">Customer hasn't uploaded Aadhaar yet</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Employment */}
            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Employment & Income</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Employment Type</p>
                  <p className="text-sm font-medium text-gray-900 capitalize mt-0.5">{(profile.employment_type || 'N/A').replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Employer</p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">{profile.employer_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Monthly Income</p>
                  <p className="text-sm font-medium text-gray-900 mt-0.5">{profile.monthly_income ? formatCurrency(profile.monthly_income) : 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Subscription */}
            {subscriptions?.length > 0 && (
              <div className="border-t border-gray-100 pt-6">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Subscription</h3>
                {subscriptions.map((sub, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{sub.plan_name || 'EMI Support Plan'}</p>
                      <p className="text-xs text-gray-500">{formatCurrency(sub.amount)} • {formatDate(sub.created_at)}</p>
                    </div>
                    <StatusBadge status={sub.payment_status || 'pending'} />
                  </div>
                ))}
              </div>
            )}

            {/* Agent Reference */}
            {agent && (
              <div className="border-t border-gray-100 pt-6">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Referred By Agent</h3>
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <Users className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                    <p className="text-xs text-gray-500">{agent.phone} • Code: {agent.referral_code}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'applications' && (
          <div className="divide-y divide-gray-100">
            {(!applications || applications.length === 0) ? (
              <div className="p-8 text-center text-gray-500 text-sm">No applications found</div>
            ) : applications.map(app => (
              <div key={app.id} className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <span className="text-xs font-mono text-gray-400">{app.application_number}</span>
                    <p className="text-lg font-bold text-gray-900">{formatCurrency(app.amount_requested || app.loan_amount)}</p>
                  </div>
                  <StatusBadge status={app.status} size="md" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-gray-500 mt-3">
                  <span>Tenure: {app.tenure || 1} months</span>
                  <span>EMI: {formatCurrency(app.emi_amount)}</span>
                  <span>Interest: {app.interest_rate || 0}%</span>
                  <span>Submitted: {formatDate(app.submitted_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'repayments' && (
          <div className="divide-y divide-gray-100">
            {(!repayments || repayments.length === 0) ? (
              <div className="p-8 text-center text-gray-500 text-sm">No repayments found</div>
            ) : repayments.map(r => (
              <div key={r.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                    r.status === 'paid' ? 'bg-green-100' : r.status === 'overdue' ? 'bg-red-100' : 'bg-amber-100'
                  }`}>
                    {r.status === 'paid' ? <CheckCircle className="h-4 w-4 text-green-600" /> :
                     r.status === 'overdue' ? <AlertTriangle className="h-4 w-4 text-red-600" /> :
                     <Clock className="h-4 w-4 text-amber-600" />}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(r.amount)}</p>
                    <p className="text-xs text-gray-500">Due: {formatDate(r.due_date)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <StatusBadge status={r.status} />
                  {r.paid_at && <p className="text-[10px] text-gray-400 mt-1">Paid: {formatDate(r.paid_at)}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="divide-y divide-gray-100">
            {(!documents || documents.length === 0) ? (
              <div className="p-8 text-center text-gray-500 text-sm">No documents uploaded</div>
            ) : documents.map(doc => (
              <div key={doc.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <FileText className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 capitalize">{(doc.doc_type || 'document').replace('_', ' ')}</p>
                    <p className="text-xs text-gray-500">{doc.file_url ? 'Uploaded' : 'Pending'}</p>
                  </div>
                </div>
                {doc.file_url && (
                  <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-blue-600 font-semibold hover:underline">
                    <Eye className="h-3 w-3" /> View
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'emis' && (
          <div className="divide-y divide-gray-100">
            {(!emi_schedules || emi_schedules.length === 0) ? (
              <div className="p-8 text-center text-gray-500 text-sm">No EMI schedule found</div>
            ) : emi_schedules.map((emi, i) => (
              <div key={emi.id || i} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-blue-600">#{emi.installment_number}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(emi.amount)}</p>
                    <p className="text-xs text-gray-500">Due: {formatDate(emi.due_date)} {emi.application_number && `• ${emi.application_number}`}</p>
                  </div>
                </div>
                <StatusBadge status={emi.status || 'pending'} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
